/**
 * Submit a queueable mutation without ever blocking the user on the network.
 *
 * Every crew action (route progress, acknowledge, signatures, work ticket,
 * cancel, time clock) applies an optimistic cache update immediately and is
 * queued + replayed by urql's offlineExchange. The only thing we must avoid is
 * making the user *wait* on a request that may hang — which happens not just
 * when fully offline, but also on a present-but-terrible connection.
 *
 * This races the mutation against a short grace window:
 *  - offline               → `queued` immediately (we know it won't resolve)
 *  - online + settles fast → `ok` / `error` (immediate confirmation on a good
 *                            connection, including real server-side errors)
 *  - online + too slow     → `queued` after `graceMs` (stop blocking; the
 *                            mutation keeps going in the background and syncs)
 *
 * The mutation is never cancelled — `exec` keeps running regardless of outcome.
 */

export const GRACE_MS = 2000

type WithError = { error?: { message?: string } | null }

export type SubmitOutcome<T> =
  | { status: 'ok'; result: T }
  | { status: 'error'; result: T }
  | { status: 'queued' }

export const submitQueueable = async <T extends WithError>(
  exec: Promise<T>,
  opts: { isOnline: boolean; graceMs?: number },
): Promise<SubmitOutcome<T>> => {
  if (!opts.isOnline) return { status: 'queued' }

  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), opts.graceMs ?? GRACE_MS)
  })
  // urql mutations resolve with { error } rather than rejecting, but guard the
  // rejection path too so a thrown network error still settles cleanly.
  const settled = exec
    .then((result) => ({ result }))
    .catch((error) => ({ result: { error } as T }))

  const winner = await Promise.race([settled, timeout])
  if (timer) clearTimeout(timer)

  if (winner === 'timeout') return { status: 'queued' }
  return winner.result.error
    ? { status: 'error', result: winner.result }
    : { status: 'ok', result: winner.result }
}
